import { createAsyncThunk } from '@reduxjs/toolkit';

import type { GatewayRanking } from 'commonTypes/api/gatewayRanking';

import { fetchOffersDataApi } from 'shared/api/offers';
import { sendSentryClientErrorOnce } from 'shared/lib';

import type { TCreditFormState } from 'entities/creditForm';
import { selectAllFormFields } from 'entities/creditForm';
import { setGroups } from 'entities/groups/model/groups.slice';
import { setIsAvailableMpl, setIsErrorResponse, setLoading, setOffers } from 'entities/offers/model/offers.slice';
import type { TOffersFiltersState } from 'entities/offersFilters';
import { offersFiltersSelector } from 'entities/offersFilters';
import type { TUserScoreState } from 'entities/scoring';
import type { TUserState } from 'entities/user/model/user.selectors';
import { whiteLabelAffIdSelector, wlBlockFFSelector } from 'entities/whiteLabels';

import { mergeBlockFF } from 'features/FetchOffersData/lib/mergeBlockFF';

import { formDataToInitialDataBodyAdapter } from './formDataToIntialDataBodyAdapter';

type TFetchOffersThunkArgs = {
  isUseMpl?: boolean;
  isAutosend?: boolean;
  variant?: number;
  activeGroupKey?: GatewayRanking.TGroupType;
  isFilterPreApprovals?: boolean;
};

export const fetchOffersThunk = createAsyncThunk<
  { isAvailableMpl?: boolean },
  TFetchOffersThunkArgs | undefined,
  { state: (TCreditFormState & TUserState & TOffersFiltersState & TUserScoreState) | TWhiteLabel }
>('fetchOffers/fetchOffersThunk', async (flowBlockFF, { dispatch, getState }) => {
  const formFields = selectAllFormFields(getState());
  const filters = offersFiltersSelector(getState());
  const partnerId = whiteLabelAffIdSelector(getState());
  const blockWllFF = wlBlockFFSelector(getState());
  const { variant, activeGroupKey, isFilterPreApprovals, ...blockFF } = flowBlockFF || {};

  dispatch(setLoading(true));
  dispatch(setIsErrorResponse(false));
  try {
    const response = await dispatch(
      fetchOffersDataApi.endpoints.getOffers.initiate(
        formDataToInitialDataBodyAdapter(
          formFields,
          { ...filters, isFilterPreApprovals },
          partnerId,
          mergeBlockFF(blockWllFF, blockFF),
          variant,
        ),
      ),
    ).unwrap();

    if (response && 'offers' in response && 'groups' in response) {
      const {
        // isAvailableMpl,
        offers,
        groups,
        calcId,
        isBentoRankingModel,
        offersStatuses = {},
        banksWithActiveStatuses = [],
        availableBanks = [],
        hiddenOffers = [],
      } = response;

      dispatch(setIsAvailableMpl(false));
      dispatch(
        setOffers({
          offers,
          calcId,
          isBentoRankingModel,
          offersStatuses,
          banksWithActiveStatuses,
          availableBanks,
          activeGroupKey,
          hiddenOffers,
        }),
      );
      dispatch(setGroups({ groups }));
    }
    dispatch(setLoading(false));

    if (response.isAvailableMpl && blockFF?.isUseMpl) {
      dispatch(setIsAvailableMpl(true));
      return { isAvailableMpl: true };
    }

    return {};
  } catch (error) {
    sendSentryClientErrorOnce('fetchOffersThunk', error);
    dispatch(setIsErrorResponse(true));
    dispatch(setLoading(false));
  }

  return {};
});
